import { call, put, takeLatest, takeEvery, select } from "redux-saga/effects";
import toast from "react-hot-toast";
import * as types from "../actions/actionTypes";
import { axiosGet, axiosPost, axiosPut, axiosDelete } from "../../utils/common/axios";
import { showDeleteToast } from "../../utils/common/toastHelpers";
import { extractCompanyId } from "../../utils/userRole";
import { 
  getAllProjects, getAllProjectsResponse, getProjectByIdResponse, getProjectByCompanyIdResponse, getSearchDataResponse, getProjectByCompanyId 
} from "../actions";

export function* createProjectEffectSaga(action) {
  try {
    const bodyPayload = action.data?.payload || action.payload || action.data;
    const { data } = yield call(() => axiosPost(`project`, bodyPayload));
    let companyId = action.data?.payload?.companyId;
    if (!companyId) {
      const authState = yield select((state) => state.auth);
      companyId = extractCompanyId(authState);
    }
    yield put(getAllProjects());
    if (companyId) {
      yield put(getProjectByCompanyId({ companyId }));
    }
    action.data?.callback?.success?.(data);
    toast.dismiss(); toast.success(data?.message || "Project Added Successfully", {
      icon: (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
            borderRadius: 8,
            background: "linear-gradient(135deg, #34d399, #0d9488)",
            flexShrink: 0,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="M20 6 9 17l-5-5"
              stroke="#fff"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      ),
      style: {
        background: "#fff",
        color: "#111827",
        fontWeight: 600,
        border: "1px solid #99f6e4",
        borderRadius: "12px",
        padding: "12px 16px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      },
    });
  } catch (e) {
    console.log(e);
    if (e?.status !== 401) action.data?.callback?.failure?.(e);
  }
}

export function* getAllProjectsEffectSaga(action) {
  try {
    const { data } = yield call(() => axiosGet(`project`));
    yield put(getAllProjectsResponse(data));
    action?.payload?.callback?.success?.(data);
  } catch (e) {
    console.log(e);
    yield put(getAllProjectsResponse([]));
  }
}

export function* getProjectByIdEffectSaga(action) {
  try {
    const { data } = yield call(() => axiosGet(`project/${action.payload?.id}`));
    yield put(getProjectByIdResponse(data));
    action?.payload?.callback?.success?.(data);
  } catch (e) {
    console.log(e);
    yield put(getProjectByIdResponse([]));
  }
}

export function* updateProjectEffectSaga(action) {
  try {
    const { data } = yield call(() =>
      axiosPut(`project/${action.data?.payload?.id}`, action.data?.payload)
    );
    let companyId = action.data?.payload?.companyId;
    if (!companyId) {
      const authState = yield select((state) => state.auth);
      companyId = extractCompanyId(authState);
    }
    yield put(getAllProjects());
    if (companyId) {
      yield put(getProjectByCompanyId({ companyId }));
    }
    action.data?.callback?.success?.(data);
    toast.dismiss(); toast.success(data?.message || "Project Edited Successfully", {
      icon: (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
            borderRadius: 8,
            background: "linear-gradient(135deg, #34d399, #0d9488)",
            flexShrink: 0,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="M20 6 9 17l-5-5"
              stroke="#fff"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      ),
      style: {
        background: "#fff",
        color: "#111827",
        fontWeight: 600,
        border: "2px solid #3b82f6",
        borderRadius: "12px",
        padding: "12px 16px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      },
    });
  } catch (e) {
    console.log(e);
    if (e?.status !== 401) action.data?.callback?.failure?.(e);
  }
}

export function* deleteProjectEffectSaga(action) {
  try {
    const { data } = yield call(() => axiosDelete(`project/${action.payload?.id}`));
    let companyId = action.payload?.companyId;
    if (!companyId) {
      const authState = yield select((state) => state.auth);
      companyId = extractCompanyId(authState);
    }
    yield put(getAllProjects());
    if (companyId) {
      yield put(getProjectByCompanyId({ companyId }));
    }
    action.payload?.callback?.success?.(data);
    showDeleteToast(data?.message || "Project deleted successfully");
  } catch (e) {
    console.log(e);
    if (e?.status !== 401) action.payload?.callback?.failure?.(e);
  }
}

export function* getProjectByCompanyIdEffectSaga(action) {
  try {
    let companyId = action?.payload?.companyId;
    if (!companyId) {
      const authState = yield select((state) => state.auth);
      companyId = extractCompanyId(authState);
    }
    const { data } = yield call(() => axiosGet(`project/company/${companyId}`));
    yield put(getProjectByCompanyIdResponse(data));
    action?.payload?.callback?.success?.(data);
  } catch (e) {
    console.log(e);
    yield put(getProjectByCompanyIdResponse([]));
  }
}

export function* getSearchDataEffectSaga(action) {
  try {
    let { companyId, searchKey, callback } = action.payload || {};
    if (!companyId) {
      const authState = yield select((state) => state.auth);
      companyId =
        authState?.loginUser?.companyId ??
        authState?.loginUser?.company?.id ??
        authState?.loginUser?.companyId?.[0] ??
        authState?.companyId;
    }

    if (!companyId || !searchKey) {
      yield put(getSearchDataResponse([]));
      callback?.success?.([]);
      return;
    }
    const { data } = yield call(() =>
      axiosGet(`project/get-search-data`, {
        params: { companyId, searchKey },
      })
    );
    yield put(getSearchDataResponse(data));
    callback?.success?.(data);
  } catch (e) {
    console.log(e);
    yield put(getSearchDataResponse([]));
    if (e?.status !== 401) action?.payload?.callback?.failure?.(e);
  }
}

export function* projectsWatcherSaga() {
  yield takeLatest(types.CREATE_PROJECT, createProjectEffectSaga);
  yield takeLatest(types.GET_ALL_PROJECTS, getAllProjectsEffectSaga);
  yield takeLatest(types.GET_PROJECT_BY_ID, getProjectByIdEffectSaga);
  yield takeLatest(types.UPDATE_PROJECT, updateProjectEffectSaga);
  yield takeLatest(types.DELETE_PROJECT, deleteProjectEffectSaga);
  yield takeEvery(types.GET_PROJECT_BY_COMPANY_ID, getProjectByCompanyIdEffectSaga);
  yield takeLatest(types.GET_SEARCH_DATA, getSearchDataEffectSaga);
}
